// What PART of an order each entry door contributes.
//
// The door is answered once, as the quote is minted (QuoteNew): it decides what
// the order STARTS with. A later arrival through a door on that same order adds
// nothing; it only selects the door's item (lineServesDoor, read by QuoteEdit).
// It never decides what the order may CONTAIN — every door
// offers every product family (ADR-018 Decision 8), so a quote begun through
// Print can still hold media and kitting work, and reaches Nexus as the one
// contract either way.

import { type EntryDivision, type FieldValues, type ProductType } from 'shared';

// The line a door contributes, or `null` for a door that contributes no line.
// Print starts a line with a blank config so STEP 1's product-type picker shows;
// Media and Assembly pre-select their family and leave the picker up. Technology
// is a request rather than an order (ADR-015 §9) and has no order surface at all.
//
// The Assembly door seeds ONE assembly item — the same `assembly` tile the grid
// offers, holding its kit at the line's own config.assembly — because that item
// is the only assembly flow there is (ADR-020). Like Media it does not stamp
// DOOR_PRESELECTED_PRODUCT_TYPE_KEY: the tile is pre-picked, not locked, so a
// customer who arrived on the Assembly door can still say the job is something
// else.
//
// The Media door lands on the media tile with USB, the commoner format, already
// answered, and leaves the picker up. It does NOT stamp
// DOOR_PRESELECTED_PRODUCT_TYPE_KEY: the customer can still change the format to
// a disc (USB is DUPLICATED in house, discs are REPLICATED out of house) or the
// item to something else. The marker rides along because it is what `usb`
// implies (mediaUseForConfig), so the media spec opens on the first render.
//
// A Record, not a ternary, so an appended door has to answer this.
export const DOOR_SEED_LINE_CONFIG: Record<EntryDivision, FieldValues | null> = {
  print: {},
  media: { product_type: 'media_replication', media_type: 'usb', media_use: 'usb' },
  assembly: { product_type: 'assembly' },
  technology: null,
};

// Which door's item each product type is. Read only to LAND a door on an order
// that already exists (QuoteEdit), never to refuse or reshape what it holds
// (ADR-015 §4). `something_else` is no door's item, and Technology seeds no line.
// A Record, so an appended product type has to answer this.
const PRODUCT_TYPE_DOOR: Record<ProductType, EntryDivision | null> = {
  perfect_bound_book: 'print',
  hard_bound_book: 'print',
  saddle_stitch_book: 'print',
  coil_or_wire_o_book: 'print',
  loose_leaf: 'print',
  single_sheet: 'print',
  labels: 'print',
  media_replication: 'media',
  // Custom packaging is picked off the full grid the Print door seeds.
  custom_packaging: 'print',
  assembly: 'assembly',
  something_else: null,
};

// Is this line the item `door` stands for: the one a re-entry through that door
// selects? A line with no product type yet is still on the product grid, which
// is the item the Print door seeds above.
export function lineServesDoor(door: EntryDivision, config: FieldValues): boolean {
  const productType = config['product_type'];
  if (typeof productType !== 'string' || productType === '') return door === 'print';
  return (
    Object.hasOwn(PRODUCT_TYPE_DOOR, productType) &&
    PRODUCT_TYPE_DOOR[productType as ProductType] === door
  );
}
